import {
  useGetSubscription,
  useListBillingPlans,
  useListPaymentMethods,
} from "@/api/stripe";
import { useOrgUsers, usePendingInvites } from "@/api/users";
import {
  LcBadge,
  STACKED_DIALOG_Z,
} from "@/components/AdminPanel/Organization/components/manage-pane-primitives";
import {
  PaymentMethodDialog,
  type PaymentMethodDialogSession,
} from "@/components/billing/PaymentMethodDialog";
import { EYEBROW, HOVER_T } from "@/components/rail-kit/registers";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ROUTES } from "@/constants/routes";
import { useInvoicePreview } from "@/hooks/useInvoicePreview";
import { useIsSelfServeBilling } from "@/hooks/useIsSelfServeBilling";
import { cn } from "@/utils";
import { formatCurrency, formatCurrencyFromCents } from "@/utils/currency";
import { describePaymentMethod } from "@/utils/paymentMethod";
import type { PlanSelection, TransformedPlan } from "@/utils/planChange";
import {
  selectionEndsTrial,
  transformBillingPlans,
  TRIAL_MAX_SEATS,
} from "@/utils/planChange";
import { BillingInterval } from "@cohesive/api-client";
import { Spinner } from "@cohesive/ui-components";
import { Minus, Plus } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

interface PlanConfigurationModalProps {
  /** The plan the dialog opens on; the customer can move to another tier from here. */
  plan: TransformedPlan;
  open: boolean;
  buttonLoading?: boolean;
  redirectTo?: string;
  /** Stacking rung when opened from inside another dialog. Defaults to the base `z-50`. */
  zIndexClassName?: string;
  onComplete: (selection: PlanSelection) => void;
  onClose: () => void;
}

/**
 * The plan you are on, as a receipt: tier, seats, billing cycle, what it
 * costs and what leaves your account today. Every number above the fold, one
 * column.
 */
export function PlanConfigurationModal({
  plan: initialPlan,
  open,
  buttonLoading = false,
  redirectTo,
  zIndexClassName,
  onComplete,
  onClose,
}: PlanConfigurationModalProps) {
  const { data: subscription } = useGetSubscription();
  const { data: paymentMethods } = useListPaymentMethods();
  const { data: billingPlans } = useListBillingPlans();
  const isSelfServeBilling = useIsSelfServeBilling();
  const current = subscription?.data.stripe_subscription;

  // Tiers come from the API so their prices and credits never drift from
  // what checkout sells; the opener's plan stands in until they load.
  const plans = useMemo(
    () => transformBillingPlans(billingPlans?.data.billing_plans),
    [billingPlans],
  );
  const [planName, setPlanName] = useState(initialPlan.name);
  // The members surface keeps this modal mounted across closes, so a tier
  // picked and then abandoned would still be selected on the next open.
  useEffect(() => {
    if (open) setPlanName(initialPlan.name);
  }, [open, initialPlan.name]);
  const plan = plans.find((p) => p.name === planName) ?? initialPlan;
  const currentPlan = plans.find((p) => p.name === current?.plan_name);
  const showTiers = isSelfServeBilling && plans.length > 1;

  // The customer's own interval first: preferring yearly moved a monthly
  // customer onto annual billing with an update they made for seats.
  const initialBillingCycle = (
    interval: BillingInterval | undefined,
  ): BillingInterval => {
    if (interval && plan.available_intervals.includes(interval)) {
      return interval;
    } else if (plan.available_intervals.includes("year")) {
      return "year";
    } else if (plan.available_intervals.includes("month")) {
      return "month";
    }
    return "year"; // fallback
  };

  const [billingCycle, setBillingCycle] = useState<BillingInterval>(() =>
    initialBillingCycle(current?.billing_interval),
  );
  const { data: orgUsers = [], isLoading: isOrgUsersLoading } = useOrgUsers();
  const { data: pendingInvites = [], isLoading: isPendingInvitesLoading } =
    usePendingInvites();

  const isUserCountLoading = isOrgUsersLoading || isPendingInvitesLoading;

  // Calculate current total users (active + pending) - this becomes the minimum
  const currentUserCount = orgUsers.length + pendingInvites.length;
  const minimumUsers = Math.max(1, currentUserCount);
  const [userCount, setUserCount] = useState(() => current?.seat_count ?? 0);

  const seatsTouched = useRef(false);
  const cycleTouched = useRef(false);
  const adoptedSubscription = useRef(false);

  // useManagePlan holds this modal back until the subscription resolves, but a
  // caller that mounts earlier must not be left configuring seats and an
  // interval nobody bought. Adopt them the first time they arrive, and only
  // where the customer has not already moved them.
  useEffect(() => {
    if (adoptedSubscription.current || !current) return;
    adoptedSubscription.current = true;
    if (!seatsTouched.current) setUserCount(current.seat_count);
    if (!cycleTouched.current) {
      setBillingCycle(initialBillingCycle(current.billing_interval));
    }
  }, [current]);

  // Update userCount when minimum changes
  useEffect(() => {
    if (userCount < minimumUsers) {
      setUserCount(minimumUsers);
    }
  }, [minimumUsers, userCount]);

  // A plan the selected interval is not sold on leaves an unreachable
  // selection; one that still sells it keeps whatever is showing.
  useEffect(() => {
    if (!plan.available_intervals.includes(billingCycle)) {
      setBillingCycle(initialBillingCycle(current?.billing_interval));
    }
  }, [plan, billingCycle, current]);

  // Check if user has a yearly subscription - if so, they can't switch to monthly
  const isYearlySubscription = current?.billing_interval === "year";

  // Show both options only if:
  // 1. User doesn't have a yearly subscription constraint, AND
  // 2. The plan has both billing intervals available
  const showBothOptions =
    !isYearlySubscription &&
    plan.available_intervals.includes("year") &&
    plan.available_intervals.includes("month");

  // Use the invoice preview hook (now handles all fallback logic internally)
  const {
    totalPrice,
    pricePerSeat: currentPricePerSeat,
    discount,
    isLoading: showLoading,
    lineItems,
  } = useInvoicePreview(
    {
      planName: plan.name,
      billingInterval: billingCycle,
      seatCount: userCount,
      enabled: open, // Only preview when modal is open
    },
    {
      debounceMs: 500,
      autoPreview: true,
    },
  );

  const handleUserCountChange = (change: number) => {
    seatsTouched.current = true;
    const newCount = Math.max(minimumUsers, userCount + change);
    setUserCount(newCount);
  };

  const endsTrial = selectionEndsTrial(
    { plan, billing_cycle: billingCycle, user_count: userCount },
    subscription?.data.stripe_subscription,
  );

  // Same precedence as buildPlanChangeAction, so the button and the action it
  // triggers agree on whether there is a card.
  const defaultPaymentMethod = paymentMethods?.data.payment_methods?.find(
    (method) => method.default,
  );
  const hasPaymentMethod = Boolean(
    defaultPaymentMethod?.id ?? current?.default_payment_method_id,
  );
  const isTrial = current?.status === "trialing";

  const perSeatOn = (tier: TransformedPlan) =>
    billingCycle === "year"
      ? (tier.yearly_price ?? 0) / 12
      : (tier.monthly_price ?? 0);
  const tierChanged = current != null && plan.name !== current.plan_name;
  const tierDirection: "upgrade" | "downgrade" | null =
    tierChanged && currentPlan
      ? perSeatOn(plan) >= perSeatOn(currentPlan)
        ? "upgrade"
        : "downgrade"
      : null;
  // A paid downgrade is scheduled for the period end rather than applied;
  // during a trial the prices just swap.
  const schedulesDowngrade = tierDirection === "downgrade" && !isTrial;
  const scheduledPlanName = current?.scheduled_plan_name;

  const recurringTotal = currentPricePerSeat * userCount;
  // useInvoicePreview asks for the preview with trial_end_now: false and then
  // discards a genuine $0 amount_due, falling back to the recurring total — so
  // a trialing customer who owes nothing was shown the full amount.
  const dueToday =
    (isTrial && !endsTrial) || schedulesDowngrade ? 0 : totalPrice;
  const intervalNoun = billingCycle === "year" ? "year" : "month";
  const billedAdverb = billingCycle === "year" ? "annually" : "monthly";

  const annualSavingsPct =
    plan.monthly_price && plan.yearly_price
      ? // Floor, not round: 990/12 against 99 is 16.67%, and the badge has read
        // "Save 16%" since it was hard-coded.
        Math.floor((1 - plan.yearly_price / 12 / plan.monthly_price) * 100)
      : 0;

  const periodEndsOn = current?.current_period_end
    ? new Date(current.current_period_end).toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;

  // Stripe's preview carries $0 scaffolding rows ("Free trial for 0 × Credit")
  // and a quantity the description never mentions. Drop one, fold the other in.
  const previewLines = useMemo(
    () =>
      lineItems
        .filter((line) => line.amount !== 0)
        .map((line) => ({
          amount: line.amount,
          description:
            line.quantity > 1
              ? `${line.quantity} × ${line.description}`
              : line.description,
        })),
    [lineItems],
  );

  // Stripe's lines earn their place only by explaining a gap between the
  // recurring price and what leaves the account today: a proration, a credit.
  const showPreviewLines = dueToday !== 0 && dueToday !== recurringTotal;

  // Re-picking the current plan while a switch is pending calls it off.
  const cancelsScheduledSwitch = !tierChanged && Boolean(scheduledPlanName);

  const buttonText = !hasPaymentMethod
    ? "Continue to payment"
    : isTrial && endsTrial
      ? "Update plan and end trial"
      : cancelsScheduledSwitch
        ? `Keep ${plan.display_name}`
        : schedulesDowngrade
          ? "Schedule plan change"
          : "Update plan";

  const periodEndPhrase = periodEndsOn
    ? `on ${periodEndsOn}`
    : "at the end of your current billing period";

  // An upgrade bills now; a downgrade waits for the period to end so the
  // customer keeps what they paid for. Neither touches a running trial.
  const tierNote = cancelsScheduledSwitch
    ? `Keeps ${plan.display_name} and cancels the switch to ${current?.scheduled_plan_display_name} scheduled ${periodEndPhrase}.`
    : !tierChanged
      ? null
      : isTrial
        ? `Switches now. Your trial continues on ${plan.display_name} with ${plan.included_credits_per_seat.toLocaleString()} credits per seat.`
        : schedulesDowngrade
          ? `${plan.name === scheduledPlanName ? "Already scheduled: applies" : "Applies"} ${periodEndPhrase}. You keep ${currentPlan?.display_name ?? "your current plan"} and its credits until then.`
          : `Applies now, prorated for the rest of this cycle. ${plan.included_credits_per_seat.toLocaleString()} credits per seat from today.`;

  const dueTodayNote =
    isTrial && !endsTrial
      ? periodEndsOn
        ? `$0 today. Your trial runs to ${periodEndsOn}; then ${formatCurrency(recurringTotal)} per ${intervalNoun}.`
        : `$0 today. ${formatCurrency(recurringTotal)} per ${intervalNoun} when the trial ends.`
      : endsTrial
        ? `Ends your trial. Billed ${billedAdverb} from today.`
        : schedulesDowngrade
          ? `$0 today. ${formatCurrency(recurringTotal)} per ${intervalNoun} from ${periodEndsOn ?? "the end of your current billing period"}.`
          : dueToday === recurringTotal
            ? `Billed ${billedAdverb} starting today.`
            : `Prorated for the rest of this cycle, then ${formatCurrency(recurringTotal)} per ${intervalNoun}.`;

  const [paymentDialog, setPaymentDialog] = useState<{
    mode: "collect" | "update";
    key: number;
  } | null>(null);
  const openPaymentDialog = (mode: "collect" | "update") =>
    setPaymentDialog((previous) => ({ mode, key: (previous?.key ?? 0) + 1 }));

  // The payment dialog's lifetime is this modal's. The modal stays mounted
  // through a close (keyed per open), so a route-sync close — browser Back —
  // would otherwise leave the card form open over a page with no Settings
  // behind it.
  useEffect(() => {
    if (!open) setPaymentDialog(null);
  }, [open]);

  const handleClose = () => {
    setPaymentDialog(null);
    onClose();
  };

  const handleContinue = () => {
    // No card: the dialog's own completion writes this selection with the card
    // it collects, so there is nothing for onComplete to do.
    if (!hasPaymentMethod) {
      openPaymentDialog("collect");
      return;
    }
    onComplete({
      plan,
      billing_cycle: billingCycle,
      user_count: userCount,
      redirectTo,
    });
  };

  const paymentSession: PaymentMethodDialogSession | null = !paymentDialog
    ? null
    : paymentDialog.mode === "collect"
      ? {
          planName: plan.name,
          billingCycle,
          userCount,
          isPaymentMethodUpdate: false,
          // One state, three readings: `dueTodayNote` promises "$0 today",
          // `buttonText` says "Continue to payment" rather than "and end
          // trial", and the write keeps the trial. Change one, change all
          // three.
          keepTrial: isTrial && !endsTrial,
          redirectTo: redirectTo ?? ROUTES.SETTINGS.USAGE,
        }
      : current
        ? {
            planName: current.plan_name,
            billingCycle: current.billing_interval,
            userCount: current.seat_count,
            isPaymentMethodUpdate: true,
            // Changing the card mid-trial must not spend the trial.
            keepTrial: true,
            redirectTo: redirectTo ?? ROUTES.SETTINGS.USAGE,
          }
        : null;

  if (isUserCountLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent
          className={cn("!max-w-xl", zIndexClassName)}
          overlayClassName={zIndexClassName}
        >
          <DialogHeader>
            <DialogTitle>Manage plan</DialogTitle>
            <DialogDescription>
              {plan.display_name} · {formatCurrency(currentPricePerSeat)} per
              seat, billed {billedAdverb}
            </DialogDescription>
          </DialogHeader>

          {showTiers && (
            <section className="flex flex-col gap-2">
              <div className={EYEBROW}>Plan</div>
              <div
                role="radiogroup"
                aria-label="Plan"
                className="inline-flex w-fit items-center gap-1 rounded-md border border-input bg-background p-1"
              >
                {plans.map((tier) => {
                  const selected = plan.name === tier.name;
                  return (
                    <button
                      key={tier.name}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      data-testid={`plan-option-${tier.name}`}
                      onClick={() => setPlanName(tier.name)}
                      className={cn(
                        "flex cursor-pointer items-center gap-2 rounded-sm px-3 py-1.5 text-sm",
                        HOVER_T,
                        selected
                          ? "bg-n-10 text-n-95 dark:bg-n-20"
                          : "text-n-60 hover:text-n-90",
                      )}
                    >
                      {tier.display_name} · {formatCurrency(perSeatOn(tier))}
                      /seat
                      {tier.name === current?.plan_name && (
                        <LcBadge tone="neutral">Current</LcBadge>
                      )}
                      {tier.name === scheduledPlanName && (
                        <LcBadge tone="warning">Scheduled</LcBadge>
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="text-sm text-n-50">
                {plan.included_credits_per_seat.toLocaleString()} credits per
                seat each {intervalNoun}
              </p>
              {tierNote && (
                <p data-testid="tier-note" className="text-sm text-n-50">
                  {tierNote}
                </p>
              )}
            </section>
          )}

          <section className="flex flex-col gap-2">
            <div className={EYEBROW}>Seats</div>
            <div className="inline-flex w-fit items-center gap-1 rounded-md border border-input bg-background p-1">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        data-testid="user-count-minus"
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => handleUserCountChange(-1)}
                        disabled={userCount <= minimumUsers}
                      >
                        <Minus />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {userCount === minimumUsers && userCount !== 1 && (
                    <TooltipContent>
                      <p>
                        You must remove users from your account before you can
                        reduce the number of seats
                      </p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
              <Input
                data-testid="user-count-input"
                type="number"
                inputMode="numeric"
                min={minimumUsers}
                aria-label="Seats"
                value={userCount}
                onChange={(e) => {
                  seatsTouched.current = true;
                  setUserCount(
                    Math.max(
                      minimumUsers,
                      parseInt(e.target.value) || minimumUsers,
                    ),
                  );
                }}
                className="h-7 w-[4ch] border-0 bg-transparent p-0 text-center text-base shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <Button
                data-testid="user-count-plus"
                variant="ghost"
                size="icon-sm"
                onClick={() => handleUserCountChange(1)}
              >
                <Plus />
              </Button>
            </div>
            <p className="text-sm text-n-50">
              {userCount === minimumUsers && currentUserCount > 1
                ? `${currentUserCount} members and pending invites each need a seat`
                : `${userCount} ${userCount === 1 ? "seat" : "seats"} · ${currentUserCount} in use`}
            </p>
            {endsTrial && (
              <Alert
                data-testid="trial-end-warning"
                className="py-2 px-3 border-rust/30 bg-rust/10 text-rust dark:border-rust/40 dark:bg-rust/10 dark:text-rust"
              >
                <AlertDescription>
                  Free trials cover up to {TRIAL_MAX_SEATS} seats. Continuing
                  with {userCount} seats ends your trial and starts billing
                  immediately.
                </AlertDescription>
              </Alert>
            )}
          </section>

          {showBothOptions && (
            <section className="flex flex-col gap-2">
              <div className={EYEBROW}>Billing cycle</div>
              <div
                role="radiogroup"
                aria-label="Billing cycle"
                className="inline-flex w-fit items-center gap-1 rounded-md border border-input bg-background p-1"
              >
                {(["month", "year"] as const).map((interval) => {
                  const selected = billingCycle === interval;
                  const perSeat =
                    interval === "year"
                      ? (plan.yearly_price ?? 0) / 12
                      : (plan.monthly_price ?? 0);
                  return (
                    <button
                      key={interval}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      data-testid={
                        interval === "year"
                          ? "annual-plan-option"
                          : "monthly-plan-option"
                      }
                      onClick={() => {
                        cycleTouched.current = true;
                        setBillingCycle(interval);
                      }}
                      className={cn(
                        "flex cursor-pointer items-center gap-2 rounded-sm px-3 py-1.5 text-sm",
                        HOVER_T,
                        selected
                          ? "bg-n-10 text-n-95 dark:bg-n-20"
                          : "text-n-60 hover:text-n-90",
                      )}
                    >
                      {interval === "year" ? "Annual" : "Monthly"} ·{" "}
                      {formatCurrency(perSeat)}/seat
                      {interval === "year" && annualSavingsPct > 0 && (
                        <LcBadge tone="active">
                          Save {annualSavingsPct}%
                        </LcBadge>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          <section
            data-testid="summary-section"
            className="rounded-lg border border-n-10 bg-n-05 p-4"
          >
            <div className="flex flex-col gap-2">
              <div className="flex items-baseline justify-between gap-4 text-base">
                <span className="min-w-0 text-n-80">
                  {plan.display_name} · {userCount} ×{" "}
                  {formatCurrency(currentPricePerSeat)}
                </span>
                <span className="shrink-0 tabular-nums text-n-90">
                  {formatCurrency(recurringTotal)} / {intervalNoun}
                </span>
              </div>

              {showPreviewLines &&
                (showLoading ? (
                  <>
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-2/3" />
                  </>
                ) : (
                  previewLines.map((line, index) => (
                    <div
                      key={index}
                      className="flex items-baseline justify-between gap-4 text-sm"
                    >
                      <span className="min-w-0 text-n-60">
                        {line.description}
                      </span>
                      <span
                        className={cn(
                          "shrink-0 tabular-nums",
                          line.amount < 0 ? "text-lc-active" : "text-n-70",
                        )}
                      >
                        {line.amount < 0 ? "−" : ""}
                        {formatCurrencyFromCents(Math.abs(line.amount))}
                      </span>
                    </div>
                  ))
                ))}

              {discount > 0 && (
                <div className="flex items-baseline justify-between gap-4 text-sm">
                  <span className="text-n-60">Discount</span>
                  <span className="shrink-0 tabular-nums text-lc-active">
                    −{formatCurrency(discount)}
                  </span>
                </div>
              )}
            </div>

            <div className="mt-3 border-t border-n-10 pt-3">
              <div className="flex items-baseline justify-between gap-4">
                <span className="font-medium text-n-90">Due today</span>
                {showLoading ? (
                  <Skeleton className="h-6 w-24" />
                ) : (
                  <span className="text-lg font-medium tabular-nums text-n-90">
                    {formatCurrency(dueToday)}
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-n-50">{dueTodayNote}</p>
            </div>
          </section>

          {/* DialogFooter is flex-col-reverse sm:justify-end, which cannot hold
              a left-hand payment line. */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p data-testid="payment-method-line" className="text-sm text-n-50">
              {describePaymentMethod(defaultPaymentMethod) ??
                "No payment method on file"}
              {defaultPaymentMethod && (
                <button
                  type="button"
                  data-testid="update-payment-method"
                  className="ml-2 cursor-pointer text-rust hover:underline"
                  onClick={() => openPaymentDialog("update")}
                >
                  Update
                </button>
              )}
            </p>
            <div className="flex shrink-0 items-center justify-end gap-2">
              <Button variant="ghost" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                data-testid="continue-button"
                onClick={handleContinue}
                isLoading={buttonLoading}
                disabled={showLoading}
              >
                {buttonText}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {paymentDialog && paymentSession && (
        <PaymentMethodDialog
          key={paymentDialog.key}
          open
          zIndexClassName={STACKED_DIALOG_Z}
          session={paymentSession}
          onSaved={() => {
            // A collected card already carried this selection into its own
            // subscription write, so the modal has nothing left to apply.
            if (paymentDialog.mode === "collect") onClose();
          }}
          onOpenChange={(next) => {
            if (!next) setPaymentDialog(null);
          }}
        />
      )}
    </>
  );
}
